import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    SEASON_STATUS, SEASON_STATUS_LABELS, fetchAuctionTotalsBySeason, fetchSeasons,
} from '../../../services/seasons';
import { useNotification } from '../../../context/NotificationContext';
import NewSeasonDialog from './NewSeasonDialog';

const STATUS_CHIP_PROPS = {
    [SEASON_STATUS.ACTIEF]: { color: 'success', variant: 'filled' },
    [SEASON_STATUS.VOORBEREIDING]: { color: 'warning', variant: 'outlined' },
    [SEASON_STATUS.AFGEROND]: { color: 'default', variant: 'outlined' },
};

function formatImportDate(value) {
    if (!value) return '—';
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Het eerste scherm van het administrator-gedeelte: alle seizoenen, historisch en
 * actief. Klikken op een seizoen opent het beheer van dat seizoen.
 */
function AdminSeasons() {
    const navigate = useNavigate();
    const notify = useNotification();
    const [seasons, setSeasons] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [seasonList, auctionTotals] = await Promise.all([
                fetchSeasons(),
                fetchAuctionTotalsBySeason(),
            ]);
            setSeasons(seasonList);
            setTotals(auctionTotals);
        } catch (err) {
            console.error('Kon seizoenen niet ophalen:', err);
            notify.error('De seizoenen konden niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4">Seizoenen</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Elk seizoen bevat de stamgegevens en de kalender waar alle subleagues op spelen.
                    </Typography>
                </Box>
                <Button variant="contained" size="large" onClick={() => setDialogOpen(true)}>
                    Nieuw seizoen starten
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Seizoen</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Subleagues</TableCell>
                                <TableCell align="right">Deelnemers</TableCell>
                                <TableCell>Kalender</TableCell>
                                <TableCell>Laatste import</TableCell>
                                <TableCell sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {seasons.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                            Er zijn nog geen seizoenen. Start er een met de knop hierboven.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {seasons.map((season) => {
                                const seasonTotals = totals[season.id] || { subleagueCount: 0, participantCount: 0 };
                                const chipProps = STATUS_CHIP_PROPS[season.status] || STATUS_CHIP_PROPS[SEASON_STATUS.AFGEROND];

                                return (
                                    <TableRow
                                        key={season.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/admin/season/${season.id}/dashboard`)}
                                    >
                                        <TableCell>
                                            <Typography variant="body1" sx={{ fontWeight: season.status === SEASON_STATUS.ACTIEF ? 700 : 400 }}>
                                                {season.displayName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Sportmonks-id {season.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={SEASON_STATUS_LABELS[season.status]}
                                                {...chipProps}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{seasonTotals.subleagueCount}</TableCell>
                                        <TableCell align="right">{seasonTotals.participantCount}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color={season.roundCount ? 'text.primary' : 'text.secondary'}>
                                                {season.roundCount
                                                    ? `${season.roundCount} rondes geïmporteerd`
                                                    : 'kalender nog niet geïmporteerd'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatImportDate(season.lastImportAt || season.importDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: 'primary.main' }}>
                                            <ChevronRightIcon fontSize="small" />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Historische seizoenen blijven leesbaar maar zijn vergrendeld: importeren en de puntentelling
                wijzigen kan alleen bij een actief seizoen.
            </Typography>

            <NewSeasonDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreated={(seasonId) => {
                    setDialogOpen(false);
                    navigate(`/admin/season/${seasonId}/stamgegevens`);
                }}
            />
        </Box>
    );
}

export default AdminSeasons;
